<template>
  <div>
    <p class="lead">
      ({{ issues.length }} {{ issues.length !== 1 ? 'refunds' : 'refund' }}
      {{ issues.length !== 1 ? 'need' : 'needs' }} review)
    </p>

    <table class="table table-hover align-middle">
      <thead>
        <tr>
          <th>Refund Date</th>
          <th>Order</th>
          <th>Item</th>
          <th class="text-end">Sale</th>
          <th class="text-end">Refund</th>
          <th class="text-end">Action</th>
        </tr>
      </thead>
      <transition mode="out-in">
        <tbody v-if="loading">
          <tr><td colspan="6" class="p-5"><loading-spinner /></td></tr>
        </tbody>

        <tbody v-else-if="issues.length === 0">
          <tr>
            <td colspan="6">
              <div class="alert alert-success my-3">
                <i class="bi bi-hand-thumbs-up me-2"></i>
                No refunds need review.
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <template v-for="issue in issues" :key="issue.id">
            <tr>
              <td class="small">{{ new Date(issue.transaction_date).toLocaleDateString() }}</td>
              <td>
                <button type="button" class="btn btn-link p-0" @click="openOrder(issue.order_id)">
                  {{ issue.order_id }}
                </button>
              </td>
              <td class="small text-truncate" style="max-width: 200px" :title="issue.item_name">{{ issue.item_name }}</td>
              <td class="text-end"><money-block :dollar="issue.amount_paid" /></td>
              <td class="text-end"><money-block :dollar="-issue.transaction_amount" /></td>
              <td class="text-end">
                <div class="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-warning"
                    :disabled="!!processingId"
                    @click="handleMarkReturned(issue)"
                    title="Mark as Returned — update order status and reverse COGS"
                  >
                    <span v-if="processingId === issue.id + ':return'" class="spinner-border spinner-border-sm"></span>
                    <span v-else><i class="bi bi-arrow-return-left me-1"></i>Returned</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="!!processingId"
                    @click="openDismiss(issue)"
                    title="Dismiss — partial refund or appeasement, item not returned"
                  >
                    <i class="bi bi-eye-slash"></i>
                  </button>
                </div>
              </td>
            </tr>

            <!-- Inline dismiss row -->
            <tr v-if="dismissingId === issue.id">
              <td colspan="6" class="bg-light py-2 px-3">
                <div class="d-flex gap-2 align-items-center">
                  <span class="small text-muted text-nowrap">Not a return?</span>
                  <input
                    type="text"
                    v-model="dismissReason"
                    class="form-control form-control-sm"
                    placeholder="Reason (optional) — e.g. partial appeasement, shipping refund"
                    @keyup.enter="confirmDismiss(issue)"
                    @keyup.escape="closeDismiss"
                  />
                  <button class="btn btn-sm btn-outline-secondary text-nowrap" :disabled="!!processingId" @click="confirmDismiss(issue)">
                    <span v-if="processingId === issue.id + ':dismiss'" class="spinner-border spinner-border-sm"></span>
                    <span v-else>Apply</span>
                  </button>
                  <button class="btn btn-sm btn-link text-nowrap" @click="closeDismiss">Cancel</button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </transition>
    </table>

    <app-modal :modal-in="!!viewingOrderId" @close="closeOrder" modalClass="modal-lg">
      <template #header>Order {{ viewingOrderId }}</template>
      <div class="container">
        <platform-order :order-id="viewingOrderId" />
      </div>
    </app-modal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import MoneyBlock from '@/app/components/Utils/MoneyBlock.vue'
import AppModal from '@/app/components/Utils/AppModal.vue'
import PlatformOrder from '@/app/components/ebay/PlatformOrder.vue'

const store = useStore()
const issues = ref([])
const loading = ref(false)
const processingId = ref(null)
const dismissingId = ref(null)
const dismissReason = ref('')
const viewingOrderId = ref(null)

async function loadIssues() {
  loading.value = true
  try {
    issues.value = await store.dispatch('eshop/getRefundsForReview')
  } catch (e) {
    alert(e.message || 'Failed to load refunds')
  }
  loading.value = false
}

async function handleMarkReturned(issue) {
  processingId.value = issue.id + ':return'
  try {
    await store.dispatch('eshop/markOrderReturned', { orderId: issue.order_id })
    issues.value = issues.value.filter(i => i.id !== issue.id)
  } catch (e) {
    alert(e.message || 'Failed to mark as returned')
  }
  processingId.value = null
}

function openDismiss(issue) {
  dismissingId.value = issue.id
  dismissReason.value = ''
}

function closeDismiss() {
  dismissingId.value = null
  dismissReason.value = ''
}

async function confirmDismiss(issue) {
  processingId.value = issue.id + ':dismiss'
  try {
    await store.dispatch('eshop/ignoreTransaction', { id: issue.id, reason: dismissReason.value })
    issues.value = issues.value.filter(i => i.id !== issue.id)
    closeDismiss()
  } catch (e) {
    alert(e.message || 'Dismiss failed')
  }
  processingId.value = null
}

function openOrder(orderId) {
  viewingOrderId.value = orderId
}

function closeOrder() {
  viewingOrderId.value = null
  loadIssues()
}

onMounted(() => loadIssues())
</script>